import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Search, UserPlus, Upload, Download, Printer,
  TagIcon, X, BanIcon, Users, ChevronUp, ChevronDown, FileText,} from "lucide-react";
import {
  Avatar, Badge, Button, EmptyState, Input, Label, Modal, ModalFooter, Select, Skeleton, useToast,
} from "@/components/ui";
import { ContactImportDialog } from "@/features/contacts/ContactImportDialog";
import { NotesImportDialog } from "@/features/contacts/NotesImportDialog";
import { NewContactDialog } from "@/features/contacts/NewContactDialog";
import { LeadScoreBadge } from "@/features/contacts/LeadScoreBadge";
import { SmartListPicker } from "@/features/lists/SmartListPicker";
import { SavedViews, type ContactsViewConfig } from "@/features/lists/SavedViews";
import { useSmartList, smartListQueryParams } from "@/features/lists/SmartListContext";
import { cn } from "@/lib/utils";
import { api, ApiError } from "@/lib/api";
import { fullName, formatRelative, formatPhone } from "@/lib/format";
import type { Contact, Tag } from "@/lib/types";

// Client-side sort of the current page by lead score. The primary sort is
// server-driven (the Select), so this is a scoped, non-breaking overlay:
// clicking the Lead-score header re-orders the loaded rows without touching
// pagination or the server query. null = keep the server's order.
type LeadSort = null | "asc" | "desc";

export function ContactsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const smartList = useSmartList();
  const { listId, filter, set: setSmartList } = smartList;
  // Saved-list names, so a printed call sheet is titled "Weight loss
  // follow-up" rather than a UUID.
  const [listNames, setListNames] = useState<Record<string, string>>({});
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");

  // If the URL has ?list=…, prefer that on first mount (deep-link / share).

  useEffect(() => {
    api.get<{ lists: { id: string; name: string }[] }>("/lists")
      .then((d) => setListNames(Object.fromEntries(d.lists.map((l) => [l.id, l.name]))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const urlList = searchParams.get("list");
    if (urlList && urlList !== listId) {
      smartList.set(urlList, { match: "all", rules: [] });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);
  const [sort, setSort] = useState("created_desc");
  const [leadSort, setLeadSort] = useState<LeadSort>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [notesImportOpen, setNotesImportOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [tags, setTags] = useState<Tag[]>([]);
  const [page, setPage] = useState(0);
  const limit = 50;

  // Sync listId to URL.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (listId) next.set("list", listId); else next.delete("list");
    setSearchParams(next, { replace: true });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [listId]);

  // Debounce search.
  useEffect(() => {
    const h = setTimeout(() => setDebounced(q), 200);
    return () => clearTimeout(h);
  }, [q]);

  // Load metadata once. Custom fields are no longer needed up front — the
  // new-contact modal is intentionally minimal and the contact-detail page
  // fetches its own field list when the user opens a record.
  useEffect(() => {
    api.get<{ tags: Tag[] }>("/tags").then((d) => setTags(d.tags)).catch(() => {});
  }, []);

  const adhocActive = (filter.rules?.length ?? 0) > 0;

  const load = useMemo(() => {
    return async (resetSelection = true) => {
      setLoading(true);
      if (resetSelection) setSelected(new Set());
      try {
        const data = await api.get<{ contacts: Contact[]; total: number }>("/contacts", {
          q: debounced || undefined,
          ...smartListQueryParams(listId, filter),
          sort,
          limit,
          offset: page * limit,
        });
        setContacts(data.contacts);
        setTotal(data.total);
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : "Failed to load contacts");
      } finally {
        setLoading(false);
      }
    };
  }, [debounced, listId, filter, adhocActive, sort, page, toast]);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [debounced, listId, filter, sort, page]);

  const allChecked = contacts.length > 0 && contacts.every((c) => selected.has(c.id));
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(contacts.map((c) => c.id)));
  };
  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const onSaved = (c: Contact) => {
    setContacts((prev) => {
      const idx = prev.findIndex((x) => x.id === c.id);
      if (idx === -1) return [c, ...prev];
      const next = [...prev]; next[idx] = c; return next;
    });
  };

  const exportCsv = () => {
    const headers = ["first_name", "last_name", "email", "phone", "city", "state", "country", "lifecycle_stage", "source", "created_at"];
    const csv = [
      headers.join(","),
      ...contacts.map((c) => headers.map((h) => csvEscape(String((c as unknown as Record<string, unknown>)[h] ?? ""))).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `contacts-${Date.now()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  /**
   * A call sheet for the nurses.
   *
   * [22:48] Having built the weight-loss audience, she said: "So we would
   * print this out for the nurses. And then they're going to go through the
   * day and they're going to do their weight loss follow up." So it prints
   * as a worksheet — name, phone, last visit — with a tick box and a space
   * to write, not as a screenshot of a dark web page.
   */
  const printList = () => {
    const listName_ = listId ? listNames[listId] : null;
    const listName = listName_ ?? "Client list";
    const rows = displayContacts.map((c) => `
      <tr>
        <td class="tick"></td>
        <td class="name">${escapeHtml([c.first_name, c.last_name].filter(Boolean).join(" ") || "—")}</td>
        <td>${escapeHtml(c.phone ?? "")}</td>
        <td class="notes"></td>
      </tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8">
      <title>${escapeHtml(listName)}</title><style>
        @page { margin: 14mm; }
        body { font: 12px/1.45 -apple-system, Segoe UI, Roboto, sans-serif; color: #111; }
        h1 { font-size: 16px; margin: 0 0 2px; }
        p.meta { margin: 0 0 14px; color: #555; font-size: 11px; }
        table { width: 100%; border-collapse: collapse; }
        th { text-align: left; font-size: 10px; text-transform: uppercase;
             letter-spacing: .06em; color: #555; border-bottom: 1.5px solid #333;
             padding: 0 6px 4px; }
        td { border-bottom: 1px solid #ddd; padding: 9px 6px; vertical-align: bottom; }
        td.tick { width: 20px; }
        td.tick::before { content: ""; display: block; width: 12px; height: 12px;
                          border: 1.2px solid #666; border-radius: 2px; }
        td.name { font-weight: 600; white-space: nowrap; }
        td.notes { width: 45%; }
        tr { page-break-inside: avoid; }
      </style></head><body>
      <h1>${escapeHtml(listName)}</h1>
      <p class="meta">${displayContacts.length} client${displayContacts.length === 1 ? "" : "s"} ·
        printed ${new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>
      <table><thead><tr><th></th><th>Client</th><th>Phone</th><th>Notes / outcome</th></tr></thead>
      <tbody>${rows}</tbody></table></body></html>`;

    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { toast.error("Your browser blocked the print window — allow pop-ups and try again."); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    // Give the document a tick to lay out before the print dialog opens.
    setTimeout(() => w.print(), 250);
  };

  // Rows as shown: apply the optional client-side lead-score sort on top of
  // whatever order the server returned. When leadSort is null we render the
  // server order untouched.
  const displayContacts = useMemo(() => {
    if (!leadSort) return contacts;
    const dir = leadSort === "asc" ? 1 : -1;
    return [...contacts].sort((a, b) => ((a.lead_score ?? 0) - (b.lead_score ?? 0)) * dir);
  }, [contacts, leadSort]);

  const toggleLeadSort = () =>
    setLeadSort((prev) => (prev === "desc" ? "asc" : prev === "asc" ? null : "desc"));

  // Snapshot everything needed to reconstruct the current view for a saved view.
  const getCurrentConfig = (): ContactsViewConfig => ({ listId, filter, sort, q });

  const applyView = (cfg: ContactsViewConfig) => {
    setSmartList(cfg.listId ?? null, cfg.filter ?? { match: "all", rules: [] });
    setSort(cfg.sort ?? "created_desc");
    setQ(cfg.q ?? "");
    setPage(0);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 sm:text-2xl">Clients</h1>
          <p className="mt-1 text-sm text-zinc-500">{total.toLocaleString()} {total === 1 ? "client" : "clients"}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" leftIcon={<Upload className="h-4 w-4" />} onClick={() => setImportOpen(true)}>
            <span className="hidden sm:inline">Import</span>
          </Button>
          {/* "the most important thing to get out of blvd is chart notes and
              lab work how are we going to get that?" — next to the client
              import, because they are the same job on the same day. */}
          <Button variant="outline" size="sm" leftIcon={<FileText className="h-4 w-4" />} onClick={() => setNotesImportOpen(true)}>
            <span className="hidden sm:inline">Import notes</span>
          </Button>
          <Button variant="outline" size="sm" leftIcon={<Download className="h-4 w-4" />} onClick={exportCsv}>
            <span className="hidden sm:inline">Export</span>
          </Button>
          {/* [22:48] "So we would print this out for the nurses. And then
              they're going to go through the day and they're going to do
              their weight loss follow up." A call sheet, not a screenshot. */}
          <Button variant="outline" size="sm" leftIcon={<Printer className="h-4 w-4" />} onClick={printList}>
            <span className="hidden sm:inline">Print call sheet</span>
          </Button>
          <Button leftIcon={<UserPlus className="h-4 w-4" />} onClick={() => setNewOpen(true)}>
            New
          </Button>
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex-1 sm:max-w-sm">
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            placeholder="Search name, email, phone…"
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
        <SmartListPicker
          listId={listId}
          filter={filter}
          onChange={(lid, f) => { setSmartList(lid, f); setPage(0); }}
        />
        <SavedViews getCurrentConfig={getCurrentConfig} onApply={applyView} />
        <Select value={sort} onChange={(e) => setSort(e.target.value)} className="sm:w-44">
          <option value="created_desc">Newest first</option>
          <option value="created_asc">Oldest first</option>
          <option value="name_asc">Name A→Z</option>
          <option value="name_desc">Name Z→A</option>
          <option value="activity_desc">Last activity</option>
        </Select>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-indigo-500/30 bg-indigo-500/10 px-3 py-2">
          <p className="text-sm text-zinc-200">
            <strong className="font-semibold">{selected.size}</strong> selected
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" leftIcon={<TagIcon className="h-3.5 w-3.5" />} onClick={() => setBulkOpen(true)}>Bulk action</Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : contacts.length === 0 ? (
        <EmptyState
          icon={Users}
          title={q ? "No matching contacts" : adhocActive || listId ? "No contacts match this filter" : "No contacts yet"}
          description={q ? "Try a different search." : "Add your first contact, or import from a CSV."}
          action={
            !q && !adhocActive && !listId && (
              <div className="flex gap-2">
                <Button leftIcon={<UserPlus className="h-4 w-4" />} onClick={() => setNewOpen(true)}>Add contact</Button>
                <Button variant="outline" leftIcon={<Upload className="h-4 w-4" />} onClick={() => setImportOpen(true)}>Import CSV</Button>
              </div>
            )
          }
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-800/70">
          {/* Mobile cards */}
          <ul className="divide-y divide-zinc-800/60 sm:hidden">
            {displayContacts.map((c) => (
              <li
                key={c.id}
                onClick={() => navigate(`/contacts/${c.id}`, { state: { from: "/contacts" } })}
                className="flex cursor-pointer items-center gap-3 px-3 py-3 hover:bg-zinc-900/40"
              >
                <Avatar name={fullName(c)} email={c.email} src={c.avatar_url} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-zinc-100">
                    {fullName(c)}
                    {c.tags.some((t) => t.emoji) && (
                      <span className="ml-1" title={c.tags.filter((t) => t.emoji).map((t) => t.name).join(", ")}>
                        {c.tags.map((t) => t.emoji).filter(Boolean).join("")}
                      </span>
                    )}
                  </p>
                  <p className="truncate text-xs text-zinc-500">{c.email || formatPhone(c.phone)}</p>
                </div>
                <LeadScoreBadge score={c.lead_score} />
                <input
                  type="checkbox"
                  checked={selected.has(c.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleOne(c.id)}
                  className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-indigo-500"
                />
              </li>
            ))}
          </ul>

          {/* Desktop table */}
          <table className="hidden w-full text-sm sm:table">
            <thead>
              <tr className="border-b border-zinc-800/70 bg-zinc-900/40 text-xs uppercase tracking-wider text-zinc-500">
                <th className="w-10 px-3 py-2.5">
                  <input type="checkbox" checked={allChecked} onChange={toggleAll} className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-indigo-500" />
                </th>
                <th className="px-3 py-2.5 text-left font-medium">Name</th>
                <th className="px-3 py-2.5 text-left font-medium">Email</th>
                <th className="px-3 py-2.5 text-left font-medium">Phone</th>
                <th className="px-3 py-2.5 text-left font-medium">Marketing</th>
                <th className="px-3 py-2.5 text-left font-medium">Tags</th>
                <th className="px-3 py-2.5 text-left font-medium">Stage</th>
                <th className="px-3 py-2.5 text-left font-medium">
                  <button
                    type="button"
                    onClick={toggleLeadSort}
                    className="inline-flex items-center gap-1 font-medium uppercase tracking-wider text-zinc-500 transition-colors hover:text-zinc-300"
                    title="Sort this page by lead score"
                  >
                    Score
                    {leadSort === "desc" && <ChevronDown className="h-3 w-3 text-indigo-400" />}
                    {leadSort === "asc" && <ChevronUp className="h-3 w-3 text-indigo-400" />}
                  </button>
                </th>
                <th className="px-3 py-2.5 text-left font-medium">Activity</th>
              </tr>
            </thead>
            <tbody>
              {displayContacts.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/contacts/${c.id}`, { state: { from: "/contacts" } })}
                  className="group cursor-pointer border-b border-zinc-800/40 hover:bg-zinc-900/30"
                >
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleOne(c.id)}
                      className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-indigo-500"
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2.5 font-medium text-zinc-100">
                      <Avatar name={fullName(c)} email={c.email} src={c.avatar_url} size="sm" />
                      <div className="min-w-0">
                        <div className="truncate">
                          {fullName(c)}
                          {c.tags.some((t) => t.emoji) && <span className="ml-1">{c.tags.map((t) => t.emoji).filter(Boolean).join("")}</span>}
                        </div>
                        {c.do_not_contact && (
                          <div className="flex items-center gap-1 text-[10px] font-normal text-rose-400 mt-0.5">
                            <BanIcon className="h-3 w-3" /> DNC
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-zinc-400">{c.email || "—"}</td>
                  <td className="px-3 py-2.5 text-zinc-400">{formatPhone(c.phone) || "—"}</td>
                  {/* [04:24] Boulevard shows marketing permission as two pills,
                      lit independently. They are separate permissions in law as
                      well as in practice, so they read separately here too. */}
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1">
                      <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium",
                        c.email_opt_in ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-800 text-zinc-500")}>
                        Email
                      </span>
                      <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium",
                        c.sms_opt_in ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-800 text-zinc-500")}>
                        Text
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {c.tags.slice(0, 3).map((t) => <Badge key={t.id} color={t.color} size="xs">{t.name}</Badge>)}
                      {c.tags.length > 3 && <Badge color="zinc" size="xs">+{c.tags.length - 3}</Badge>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-zinc-400 capitalize">{c.lifecycle_stage}</td>
                  <td className="px-3 py-2.5"><LeadScoreBadge score={c.lead_score} /></td>
                  <td className="px-3 py-2.5 text-zinc-500">{c.last_activity_at ? formatRelative(c.last_activity_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && total > limit && (
        <div className="flex items-center justify-between text-sm text-zinc-500">
          <span>Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total.toLocaleString()}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
            <Button variant="outline" size="sm" disabled={(page + 1) * limit >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      {/* Minimal new-contact dialog. Anything beyond name/email/phone is
          edited inline on the contact-detail page so the create flow
          stays a fast 4-field form. */}
      <NewContactDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(c) => { onSaved(c); setNewOpen(false); navigate(`/contacts/${c.id}`, { state: { from: "/contacts" } }); }}
      />
      <ContactImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={() => load()} />
      <NotesImportDialog open={notesImportOpen} onClose={() => setNotesImportOpen(false)} onImported={() => load()} />
      <BulkActionDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        ids={Array.from(selected)}
        tags={tags}
        onDone={() => { setSelected(new Set()); load(); }}
      />
    </div>
  );
}

/** Escape text destined for the print window's HTML. */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch] as string));
}

function csvEscape(s: string): string {
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

function BulkActionDialog({
  open, onClose, ids, tags, onDone,
}: {
  open: boolean;
  onClose: () => void;
  ids: string[];
  tags: Tag[];
  onDone: () => void;
}) {
  const toast = useToast();
  const [action, setAction] = useState<"add_tag" | "remove_tag" | "set_lifecycle" | "set_do_not_contact" | "delete">("add_tag");
  const [tagName, setTagName] = useState(tags[0]?.name ?? "");
  const [lifecycle, setLifecycle] = useState("lead");
  const [busy, setBusy] = useState(false);
  // Typed-confirmation gate for the delete action. The user has to type
  // the word DELETE before the button enables — the same pattern AWS,
  // GitHub, and Stripe use for irreversible bulk actions. Without this,
  // a single muscle-memory click on "Apply" can wipe thousands of
  // contacts (and all their messages, tasks, notes, and activity).
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const deleteOk = action !== "delete" || deleteConfirm.trim().toUpperCase() === "DELETE";

  // Reset typed-confirm whenever the modal opens or the action changes.
  useEffect(() => { setDeleteConfirm(""); }, [open, action]);

  const submit = async () => {
    if (action === "delete" && !deleteOk) return;
    setBusy(true);
    try {
      const body: Record<string, unknown> = { ids, action };
      if (action === "add_tag" || action === "remove_tag") body.tag_name = tagName;
      if (action === "set_lifecycle") body.lifecycle_stage = lifecycle;
      if (action === "set_do_not_contact") body.do_not_contact = true;
      const res = await api.post<{ updated: number }>("/contacts/bulk", body);
      toast.success(`${res.updated} updated`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Bulk action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Bulk action on ${ids.length} contacts`} size="md">
      <div className="space-y-3">
        <div>
          <Label>Action</Label>
          <Select value={action} onChange={(e) => setAction(e.target.value as typeof action)}>
            <option value="add_tag">Add tag</option>
            <option value="remove_tag">Remove tag</option>
            <option value="set_lifecycle">Change lifecycle stage</option>
            <option value="set_do_not_contact">Mark as do-not-contact</option>
            <option value="delete">Delete</option>
          </Select>
        </div>
        {(action === "add_tag" || action === "remove_tag") && (
          <div>
            <Label>Tag</Label>
            <Input value={tagName} onChange={(e) => setTagName(e.target.value)} placeholder="VIP" leftIcon={<TagIcon className="h-4 w-4" />} />
          </div>
        )}
        {action === "set_lifecycle" && (
          <div>
            <Label>Stage</Label>
            <Select value={lifecycle} onChange={(e) => setLifecycle(e.target.value)}>
              {["lead", "contact", "customer", "churned"].map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </div>
        )}
        {action === "delete" && (
          <div className="space-y-2 rounded-lg border border-rose-500/30 bg-rose-500/5 p-3">
            <p className="text-sm font-medium text-rose-200">This is irreversible.</p>
            <p className="text-sm text-rose-300/90">
              You're about to permanently delete <strong>{ids.length} contact{ids.length === 1 ? "" : "s"}</strong>
              {" "}and all their messages, notes, tasks, and activity. There's no undo.
            </p>
            <div>
              <Label>Type <code className="rounded bg-rose-500/20 px-1 font-mono text-rose-200">DELETE</code> to confirm</Label>
              <Input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                autoFocus
              />
            </div>
          </div>
        )}
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button
          variant={action === "delete" ? "danger" : "primary"}
          onClick={submit}
          loading={busy}
          disabled={!deleteOk}
        >
          Apply
        </Button>
      </ModalFooter>
    </Modal>
  );
}
